import './index.css';
import React from "react"
import Message from "./components/chat/message"
import QuickMessagesMenu from "./components/chat/quickMessagesMenu"
import { Button } from "./components/ui/button"
import { Input } from "./components/ui/input"

import { MicIcon, MicOffIcon, SendIcon, PenLine, Minimize2, Download, RefreshCw, Loader2Icon } from 'lucide-react'

type ChatProps = {
  datasetId?: string
  projectId?: string
  filename: string
  loading: boolean
  messages: string[]
  latestMessageIndex: number
  quickMessages: string[]
  favoriteMessages: string[]
  showSteps: boolean
  input: string
  inputRef: React.RefObject<HTMLInputElement>
  context: string
  contextOpen: boolean
  contextRef: React.RefObject<HTMLInputElement>
  chatContainerRef: React.RefObject<HTMLDivElement>
  recordingStatus: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setContext: React.Dispatch<React.SetStateAction<string>>
  setInput: React.Dispatch<React.SetStateAction<string>>
  setQuickMessages: React.Dispatch<React.SetStateAction<any[]>>
  handleCreatePrompt: (prompt: string, context: string) => void
  handleCreateSteps: () => void
  handleClearConversation: (showToast?: boolean) => void
  handleQuickMessages: (prompt: string) => void
  handleFavorites: (prompt: string, isFavorite: boolean) => void
  handleContext: () => void
  handleOnDownloadPdf: () => void
  onSendMessage: (message: string) => void
  handleOpenModal: (imageUrl: string) => void
  startRecording: () => void
  stopRecording: () => void
}

class Chat extends React.Component<ChatProps>{
  render(){
    const { filename, loading, messages, latestMessageIndex, quickMessages, favoriteMessages, showSteps, input, inputRef, context, contextOpen, contextRef, chatContainerRef, recordingStatus, setContext, setInput, handleCreatePrompt, handleCreateSteps, handleClearConversation, handleQuickMessages, handleFavorites, handleContext, handleOnDownloadPdf, onSendMessage, handleOpenModal, startRecording, stopRecording } = this.props

  
    return (
      <div className="flex h-full flex-col pb-5">	
        <div
          ref={chatContainerRef}
          className="flex flex-1 flex-col overflow-auto px-6 pt-5"
        >
          {messages.map((message, index) => {
            const isFavorite = favoriteMessages.includes(message)
            return (
            <Message key={index} index={index} message={message} handleCreateSteps={handleCreateSteps} handleFavorites={handleFavorites} isFavorite={isFavorite} input={input} showSteps={showSteps} isLatestMessage={index === latestMessageIndex} onImageClick={handleOpenModal}/>
          )})}
  
          {messages.length % 2 !== 0 && <Message loading handleCreateSteps={handleCreateSteps} handleFavorites={handleFavorites} isFavorite={false} onImageClick={handleOpenModal} input={input} />}
        </div>
        <div className={`flex justify-between items-center px-6 opacity-80 ${contextOpen ? "" : "pb-1"}`}>
          <div className="flex gap-1 justify-start">
            <Button
              className=""
              title="Download conversation"
              variant="outline"
              size="sm"
              onClick={handleOnDownloadPdf}
              disabled={filename?.length === 0 || loading}
            >
              <Download size={14}/>
            </Button>
            <Button
              className=""
              title="Clear conversation context"
              variant="outline"
              size="sm"
              onClick={() => handleClearConversation()}
              disabled={filename?.length === 0 || loading}
            >
              <RefreshCw size={14}/>
            </Button>
            <Button
              className=""
              title="Add more context"
              variant="outline"
              size="sm"
              onClick={handleContext}
              disabled={contextOpen || filename?.length === 0 || loading}
            >
              <PenLine size={14}/>
            </Button>
          </div>
          {filename && (
            <QuickMessagesMenu handleCreatePrompt={handleCreatePrompt} handleQuickMessages={handleQuickMessages} questions={quickMessages.slice(0,5)} favorites={favoriteMessages} context={context} disabled={messages.length % 2 !== 0 || loading}/>
          )}	
        </div>
        <div className="items-center py-1 pl-6 gap-3" style={{ display: contextOpen ? 'flex' : 'none' }}>
            <Input
              ref={contextRef}
              placeholder="Additional Context"
              formNoValidate
              autoComplete="off"
              value={context}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setContext(e.currentTarget.value)
              }
              disabled={messages.length % 2 !== 0 || loading}
              className="w-10/12"
            />
            <Button
              title="Minimize context input"
              variant="secondary"
              size="sm"
              onClick={handleContext}
            >
              <Minimize2 size={14}/>
            </Button>
        </div>
        <div className="relative flex items-center px-6">
          <Input
            ref={inputRef}
            placeholder="Ask Harvest Lab AI anything"
            formNoValidate
            autoComplete="off"
            value={input}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setInput(e.currentTarget.value)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && onSendMessage(e.currentTarget.value)
            }
            disabled={(messages.length % 2 !== 0) || (filename?.length === 0) || loading}
            className="pr-12"
          />
          {recordingStatus && (
            <div className='absolute pb-1 right-20 top-3'>
              <Loader2Icon size={16} className="animate-spin" />
            </div>
          )}
          {!recordingStatus && input && (
            <Button
              onClick={() => onSendMessage(input)}
              size="icon"
              disabled={(messages.length % 2 !== 0) || (filename?.length === 0) || loading}
              className="absolute right-8 h-8 w-8"
            >
              <SendIcon size={16} />
            </Button>
          )}
          {!input && (
            <Button
              variant="outline"
              size="icon"
              className="absolute right-8 h-8 w-8"
              disabled={(messages.length % 2 !== 0) || (filename?.length === 0) || loading}
              onClick={
                recordingStatus 
                  ? stopRecording
                  : startRecording
              }
            >
              {recordingStatus ? 
                <MicOffIcon size={16} color="#d90202"/> : <MicIcon size={16} />
              }
            </Button>
          )}
        </div>
      </div >
    )
  }
}

export { Chat }